import React from 'react';
import {
    Dropdown,
    Feedback,
    MultilineDropdownMenu,
    MultilineDropdownMenuProps,
    SelectBoxProps,
    UnifiedSelectBox,
    UnifiedSelectBoxProps,
} from '@placer-ui/components';
import { Controller, useFormContext } from 'react-hook-form';
import type { ControllerProps, FieldValues } from 'react-hook-form/dist/types';
import { DropdownProps } from '@placer-ui/components/dist/core-components/menu/dropdown';

export type ControlledMultiSelectProps<T extends FieldValues = FieldValues> = Omit<
    ControllerProps<T>,
    'render'
> &
    Omit<DropdownProps, 'trigger' | 'children' | 'onClick'> &
    Omit<SelectBoxProps, 'value' | 'onClick'> &
    Omit<MultilineDropdownMenuProps, 'onSelection'> & {
        withSearch?: boolean;
    } & UnifiedSelectBoxProps;

export function ControlledMultilineDropdownMenu<T extends FieldValues = FieldValues>(
    props: ControlledMultiSelectProps<T>,
) {
    const formContext = useFormContext<T>();
    const {
        name,
        control = formContext.control,
        defaultValue,
        rules,
        disabled,
        shouldUnregister,
        required,
        items,
        label,
        menuWidth,
        asterix,
        placeholder,
        showClearIndicator,
        testId = `controlled-basic-dropdown-${name}`,
        onClearClick,
        onItemSelection,
        ...inputProps
    } = props;

    const validationRules = {
        ...rules,
        required: { value: !!required, message: 'This field is required' },
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={validationRules}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field, formState }) => {
                const errorMessage = formState.errors[field.name]?.message as string;
                const isError = Boolean(errorMessage);
                const errorsFeedback: Feedback[] | undefined = isError
                    ? [{ message: errorMessage, state: 'error', hideIcon: true }]
                    : undefined;
                const feedback = [...(inputProps.feedback || []), ...(errorsFeedback || [])];

                const triggerValue = items.find((item) => item.value === field.value);
                return (
                    <Dropdown
                        appendToParent
                        menuWidth={menuWidth}
                        closeOnSelection={inputProps.closeOnSelection}
                        testId={testId}
                        trigger={
                            <UnifiedSelectBox
                                asterix={asterix}
                                disabled={disabled}
                                isError={isError}
                                feedback={feedback}
                                value={triggerValue?.label?.toString() || ''}
                                placeholder={placeholder}
                                label={label}
                                required={false}
                                testId={`${testId}-select-box`}
                                onClearClick={
                                    showClearIndicator
                                        ? () => {
                                              field.onChange(null);
                                              onClearClick?.();
                                          }
                                        : undefined
                                }
                            />
                        }
                    >
                        <MultilineDropdownMenu
                            {...inputProps}
                            onSelection={(values) => {
                                const selectedValues = values
                                    .filter((value) => value.isSelected)
                                    .map((value) => value.value);
                                field.onChange(selectedValues[0]);
                            }}
                            items={items.map((item) => ({
                                ...item,
                                isSelected: !!(field.value as string[])?.includes(item.value),
                            }))}
                        />
                    </Dropdown>
                );
            }}
        />
    );
}
